/**
 * `/email?estimate=<id>` opens the composer on a letter that carries that quote.
 *
 * The quote page links here so the operator can write a cover letter above the
 * PDF instead of the fixed notice its one-click send mails. The server builds
 * the letter: the client's address, the subject, an opening line, and the PDF
 * already staged as this operator's attachment (`POST .../mail-draft`).
 *
 * Asked once per id, not cancelled on cleanup. The request STAGES a file, so a
 * second run of the effect (React's development double mount) would stage a
 * second copy, and a cancelled first run would open nothing at all. The
 * parameter is consumed once the request settles, like `?call=` and `?brain=`.
 */
import { useEffect, useRef } from "react";
import type { NavigateFunction } from "react-router-dom";
import { useT } from "../../../i18n/I18nProvider";
import { getEstimateMailDraft } from "../../../lib/sales";
import { useToast } from "../../ui";
import type { ComposerDraft } from "./Composer";

export function useEstimateComposeLink(
  search: string,
  navigate: NavigateFunction,
  openComposer: (d: ComposerDraft) => void,
) {
  const t = useT();
  const toast = useToast();
  const asked = useRef<string | null>(null);

  useEffect(() => {
    const id = new URLSearchParams(search).get("estimate");
    if (!id || asked.current === id) return;
    asked.current = id;
    void getEstimateMailDraft(id)
      .then((d) => {
        openComposer({
          mode: "new",
          to: d.to ? [d.to] : [],
          subject: d.subject,
          body: d.body,
          attachments: [d.attachment],
          salesDoc: { kind: "estimate", id },
        });
        if (!d.to) toast(t("email.docNoRecipient"));
      })
      .catch(() => toast(t("email.docDraftFailed")))
      .finally(() => {
        asked.current = null;
        navigate("/email", { replace: true });
      });
  }, [search, navigate, openComposer, toast, t]);
}
